/**
 * Heatmap chart builder (ADR-0079 §4.1, §4.4).
 *
 * Each row → one cell `[xCat, yCat, value]`. The renderer collects unique
 * x-axis and y-axis categories from the rows. `visualMap` drives the color
 * scale: explicit `options.visualMap` wins; otherwise auto-fit to value
 * range.
 *
 * Click contract preserved (heatmap is the second click-contract chart per
 * ADR-0079 §4.4).
 */

import type { HeatmapSpec } from "../echartsSpec.js";
import {
  buildNoDataGraphic,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type EChartsOption = Record<string, unknown>;

const DEFAULT_HEATMAP_PALETTE = [
  "#deebf7",
  "#9ecae1",
  "#4292c6",
  "#2171b5",
  "#08306b",
];

export function buildHeatmapOption(
  spec: HeatmapSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const xIdx = resolveColumnIndex(spec.encoding.x.column, dimensions);
  const yIdx = resolveColumnIndex(spec.encoding.y.column, dimensions);
  const valueIdx = resolveColumnIndex(spec.encoding.value.column, dimensions);

  const xCats: string[] = [];
  const yCats: string[] = [];
  const xSeen = new Set<string>();
  const ySeen = new Set<string>();
  // Cells: ECharts heatmap expects [xCat, yCat, value] per data point.
  const cells: Array<[string, string, number]> = [];
  let minVal = Infinity;
  let maxVal = -Infinity;

  if (xIdx !== null && yIdx !== null && valueIdx !== null) {
    for (const row of rows) {
      const x = row[xIdx] == null ? "" : String(row[xIdx]);
      const y = row[yIdx] == null ? "" : String(row[yIdx]);
      const v = coerce(row[valueIdx]);
      if (!xSeen.has(x)) { xSeen.add(x); xCats.push(x); }
      if (!ySeen.has(y)) { ySeen.add(y); yCats.push(y); }
      if (v == null) continue;
      cells.push([x, y, v]);
      if (v < minVal) minVal = v;
      if (v > maxVal) maxVal = v;
    }
  }

  const visualMapMin = spec.options.visualMap?.min ?? (Number.isFinite(minVal) ? minVal : 0);
  const visualMapMax = spec.options.visualMap?.max ?? (Number.isFinite(maxVal) ? maxVal : 1);
  const visualMapColors =
    spec.options.visualMap?.palette
    ?? resolvePalette(spec.options.color, DEFAULT_HEATMAP_PALETTE.length)
    ?? DEFAULT_HEATMAP_PALETTE;

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    legend: { show: false },
    tooltip: {
      show: spec.options.tooltip?.show !== false,
      trigger: "item",
    },
    xAxis: { type: "category", data: xCats },
    yAxis: { type: "category", data: yCats },
    visualMap: {
      min: visualMapMin,
      max: visualMapMax,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 8,
      inRange: { color: visualMapColors },
    },
    series: [
      {
        type: "heatmap",
        data: cells,
        // Preserve the click contract: encode points to dimension names so
        // Chart.tsx's resolveClickedColumn returns spec.encoding.x.column.
        encode: { x: spec.encoding.x.column, y: spec.encoding.y.column },
      },
    ],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
